import {useEffect, useState} from 'react';
import {DateTime} from 'luxon';
import {getTeamPlayerMetricsReport} from '../api/reportAPI';
import {PlayerMetricsReportPlayer, ReportMetricSummary} from '../interfaces';
import {getMean} from '../services/mathService';
import {SessionTypes} from '../enums';

export type MetricPeriod = 'season' | 'last28' | 'lastSession';
export type MetricSessionType = 'all' | 'game' | 'practice';

export interface PlayerMetricValues {
  // Player Load: cumulative3DPlayerLoadV2 / 100 (matches reportService formatting)
  playerLoad?: number;
  // Energy Ratio as a percentage (0-100)
  energyRatio?: number;
  // hustleScoreTopLevel, 1-99.
  hustle?: number;
  // Seconds; the row formats it as m:ss.
  timeOnIce?: number;
}

export interface PlayerMetricEntry {
  values: PlayerMetricValues;
  // Percent change vs. the immediately-preceding comparable window/session.
  change: {
    playerLoad?: number;
    energyRatio?: number;
    hustle?: number;
  };
}

export type PlayerMetricsMap = Record<number, PlayerMetricEntry>;

// `all` sends no filter at all so the report keeps every session type.
const SESSION_TYPE_MAP: Record<MetricSessionType, SessionTypes[] | undefined> =
  {
    all: undefined,
    game: [SessionTypes.game],
    practice: [SessionTypes.practice, SessionTypes.skills],
  };

// Approx. hockey season start: most recent September 1.
const seasonStart = (now: DateTime) => {
  const thisYearStart = DateTime.fromObject({year: now.year, month: 9, day: 1});
  return now < thisYearStart ? thisYearStart.minus({years: 1}) : thisYearStart;
};

// windowFor/buildFromWindows/buildFromLatest are exported for unit tests only.
export const windowFor = (period: 'season' | 'last28') => {
  const now = DateTime.now();
  if (period === 'last28') {
    const start = now.minus({days: 28});
    return {
      current: {start: start.toJSDate(), end: now.toJSDate()},
      prior: {start: now.minus({days: 56}).toJSDate(), end: start.toJSDate()},
    };
  }
  const start = seasonStart(now);
  const lengthDays = Math.max(1, now.diff(start, 'days').days);
  return {
    current: {start: start.toJSDate(), end: now.toJSDate()},
    prior: {
      start: start.minus({days: lengthDays}).toJSDate(),
      end: start.toJSDate(),
    },
  };
};

const load = (s?: ReportMetricSummary) =>
  s?.cumulative3DPlayerLoadV2 != null
    ? s.cumulative3DPlayerLoadV2 / 100
    : undefined;
const er = (s?: ReportMetricSummary) =>
  s?.energyRatio != null ? s.energyRatio * 100 : undefined;
const hustle = (s?: ReportMetricSummary) =>
  s?.hustleScoreTopLevel != null ? s.hustleScoreTopLevel : undefined;
// Left in seconds so the row can format it as m:ss without a round trip.
const toi = (s?: ReportMetricSummary) => s?.timeOnIce ?? undefined;

const meanValues = (player: PlayerMetricsReportPlayer): PlayerMetricValues => ({
  playerLoad: getMean((player.data ?? []).map(load)) ?? undefined,
  energyRatio: getMean((player.data ?? []).map(er)) ?? undefined,
  hustle: getMean((player.data ?? []).map(hustle)) ?? undefined,
  timeOnIce: getMean((player.data ?? []).map(toi)) ?? undefined,
});

const pctChange = (current?: number, prior?: number) => {
  if (current == null || prior == null || prior === 0) {
    return undefined;
  }
  return ((current - prior) / prior) * 100;
};

// Windowed average (season / last 28 days): mean over the window vs. the prior
// window of equal length.
export const buildFromWindows = (
  currentData: PlayerMetricsReportPlayer[],
  priorData: PlayerMetricsReportPlayer[],
): PlayerMetricsMap => {
  const prior: Record<number, PlayerMetricValues> = {};
  priorData.forEach(p => (prior[p.profileId] = meanValues(p)));
  const map: PlayerMetricsMap = {};
  currentData.forEach(p => {
    const values = meanValues(p);
    const prev = prior[p.profileId] ?? {};
    map[p.profileId] = {
      values,
      change: {
        playerLoad: pctChange(values.playerLoad, prev.playerLoad),
        energyRatio: pctChange(values.energyRatio, prev.energyRatio),
        hustle: pctChange(values.hustle, prev.hustle),
      },
    };
  });
  return map;
};

// Most recent session: latest dated entry per player vs. the one before it.
export const buildFromLatest = (
  data: PlayerMetricsReportPlayer[],
): PlayerMetricsMap => {
  const map: PlayerMetricsMap = {};
  data.forEach(p => {
    const sorted = [...(p.data ?? [])].sort((a, b) =>
      b.date.localeCompare(a.date),
    );
    const latest = sorted[0];
    const prev = sorted[1];
    const values: PlayerMetricValues = {
      playerLoad: load(latest),
      energyRatio: er(latest),
      hustle: hustle(latest),
      timeOnIce: toi(latest),
    };
    map[p.profileId] = {
      values,
      change: {
        playerLoad: pctChange(values.playerLoad, load(prev)),
        energyRatio: pctChange(values.energyRatio, er(prev)),
        hustle: pctChange(values.hustle, hustle(prev)),
      },
    };
  });
  return map;
};

const useTeamPlayerMetrics = (
  teamId: number,
  period: MetricPeriod,
  sessionType: MetricSessionType,
) => {
  const [metrics, setMetrics] = useState<PlayerMetricsMap>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const sessionTypes = SESSION_TYPE_MAP[sessionType];
        if (period === 'lastSession') {
          const now = DateTime.now();
          // Wide lookback so we reliably find the most recent session even deep
          // in the offseason (the report needs a start date; we then reduce to
          // each player's latest dated entry). 540d covers a full season + gap.
          const res = await getTeamPlayerMetricsReport({
            teamId,
            startDate: now.minus({days: 540}).toJSDate(),
            endDate: now.toJSDate(),
            sessionTypes,
          });
          if (!cancelled) {
            setMetrics(buildFromLatest(res.data ?? []));
          }
          return;
        }
        const {current, prior} = windowFor(period);
        const [currentRes, priorRes] = await Promise.all([
          getTeamPlayerMetricsReport({
            teamId,
            startDate: current.start,
            endDate: current.end,
            sessionTypes,
          }),
          getTeamPlayerMetricsReport({
            teamId,
            startDate: prior.start,
            endDate: prior.end,
            sessionTypes,
          }),
        ]);
        if (!cancelled) {
          setMetrics(
            buildFromWindows(currentRes.data ?? [], priorRes.data ?? []),
          );
        }
      } catch (e) {
        if (!cancelled) {
          setError(e);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    if (teamId) {
      run();
    }
    return () => {
      cancelled = true;
    };
  }, [teamId, period, sessionType]);

  return {metrics, loading, error};
};

export default useTeamPlayerMetrics;
